import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { OverviewComponent } from 'app/modules/admin/overview/overview.component';
import { NoPendingActionsRequiredGuard } from './core/auth/guards/noPendingActionsRequired.guard';
import { issuesHubspotBlockGuard } from './modules/admin/issues/guards/issues-access.guard';
import { LinkCompanyConfirmationHubspotComponent } from './modules/auth/link-company/link-company-confirmation-hubspot/link-company-confirmation-hubspot.component';
import { LinkCompanyConfirmationComponent } from './modules/auth/link-company/link-company-confirmation/link-company-confirmation.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Redirect empty path to '/example'
    {path: '', pathMatch : 'full', redirectTo: 'devices'},

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {path: 'signed-in-redirect', pathMatch : 'full', redirectTo: 'devices'},

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes')},
            {path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes')},
            {path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes')},
            {path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes')}
        ]
    },

    // Confirm change nif request from Hubspot.
    // What matters here is the request hash and company id, both must be present in the url as query params.
    // The user here can either be a logged-in user or not (if it is an external user) therefore no guards serve us here.
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'change-nif/confirmation', loadComponent: () => import('app/modules/admin/settings/company/change-nif-confirmation/change-nif-confirmation.component').then(m => m.ChangeNifConfirmationComponent)}
        ]
    },

    // Sign-up complete route
    // This route is used to complete the sign-up process after the user has registered.
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
                {path: 'sign-up/complete', loadComponent: () => import('app/modules/auth/sign-up/complete-sign-up/complete-sign-up.component').then(m => m.CompleteSignUpComponent) },
                {path: 'link-company/confirmation', component: LinkCompanyConfirmationComponent },
                {path: 'link-company/new-company/confirmation', component: LinkCompanyConfirmationHubspotComponent },
                {path: 'change-password', loadChildren: () => import('app/modules/auth/change-password/change-password.routes')},
                {path: 'help-center/public/apikeys', loadComponent: () => import('app/modules/admin/help-center/public/api-keys/api-keys-docs.component').then(m => m.ApiKeysDocsComponent) },
           ]
    },

    // admin no layout routes
    {
        path: '',
        canActivate: [AuthGuard, NoPendingActionsRequiredGuard],
        //canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout:'empty'
        },
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'change-nif', loadComponent: () => import('app/modules/admin/settings/company/change-nif/change-nif.component').then(m => m.ChangeNifComponent)},
            {path: 'link-company', loadChildren: () => import('app/modules/auth/link-company/link-company.routes')},
            {
                path: 'issues/add',
                canActivate: [issuesHubspotBlockGuard],
                loadComponent: () =>
                    import('app/modules/admin/issues/add-issue/add-issue.component')
                    .then(m => m.AddIssueComponent),
            },
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes')},
            {path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes')}
        ]
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes')},
        ]
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard, NoPendingActionsRequiredGuard],
        //canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'overview', component: OverviewComponent },
            {path: 'api-keys', loadChildren: () => import('app/modules/admin/api-keys/api-keys.routes')},
            {path: 'devices', loadChildren: () => import('app/modules/admin/devices/devices.routes')},
            {path: 'devices-map', loadComponent: () => import('app/modules/admin/devices/device-map/device-map.component').then(m => m.DeviceMapComponent)},
            {path: 'users', loadChildren: () => import('app/modules/admin/users/users.routes')},
            {path: 'keys', loadChildren: () => import('app/modules/admin/keys/keys.routes')},
            {path: 'scheduled-actions', loadChildren: () => import('app/modules/admin/scheduled-actions/scheduled-actions.routes')},
            {path: 'documentation', loadChildren: () => import('app/modules/admin/documentation/documentation.routes')},
            {path: 'issues', loadChildren: () => import('app/modules/admin/issues/issues.routes')},
            {path: 'reports', loadChildren: () => import('app/modules/admin/reports/reports.routes')},
            {path: 'settings', loadChildren: () => import('app/modules/admin/settings/settings.routes')},
            {path: 'help-center', loadChildren: () => import('app/modules/admin/help-center/help-center.routes')},
        ]
    },

    // Fallback when no prior route is matched
    { 
        path: '**', redirectTo: 'sign-in' 
    }
];
